/******************************************************************************
 * Angular and Third-Party Imports
 *****************************************************************************/
import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { AuthGuard } from '@auth0/auth0-angular';

/******************************************************************************
 * Application Guards
 *****************************************************************************/
import { loginGuard } from './guards/login.guard';
import { publicProfileGuard } from './guards/public-profile.guard';
import { UtmParamsGuard } from './guards/utm-params.guard';

/******************************************************************************
 * Component Imports
 *****************************************************************************/
// Public Components not lazy loaded
import { ErrorStateComponent } from './pages/public/error-state/error-state.component';
import { ForgotPasswordComponent } from './pages/public/forgot-password/forgot-password.component';
import { LayoutPublicComponent } from './layouts/layout-public/layout-public.component';
import { homeGuard } from './guards/home.guard';
import { RegisterGuard } from './guards/register.guard';
import { ApiManagementComponent } from './pages/private/settings/api-management/api-management.component';
import { isNotAccessRestrictedBillingGuard } from './guards/is-not-restricted.guard';
import { PaymentReportGuard } from './guards/payment-report.guard';
/******************************************************************************
 * Route Configuration
 *****************************************************************************/
export const routes: Routes = [
  // Error handling route
  { path: 'error', component: ErrorStateComponent },
  { path: '', canActivate: [homeGuard], pathMatch: 'full', component: LayoutPublicComponent },
  // Public routes wrapped in LayoutPublicComponent
  {
    path: '',
    component: LayoutPublicComponent,
    children: [
      // Authentication routes
      {
        path: 'register',
        canActivate: [RegisterGuard, UtmParamsGuard],
        loadComponent: () =>
          import('./pages/public/register-page/register-page.component').then((m) => m.RegisterPageComponent),
        data: { fullWidthMode: true },
        title: 'Register',
      },
      {
        path: 'activate-account',
        canActivate: [RegisterGuard, UtmParamsGuard],
        loadComponent: () =>
          import('./pages/public/register-page/register-page.component').then((m) => m.RegisterPageComponent),
        data: {
          fullWidthMode: true,
        },
        title: 'Activate Account WHMCS',
      },
      { path: 'login', canActivate: [loginGuard], title: 'Login', component: LayoutPublicComponent },
      {
        path: 'mailLink',
        title: 'Login',
        canActivate: [RegisterGuard],
        loadComponent: () => import('./pages/public/mail-link/mail-link.component').then((m) => m.MailLinkComponent),
        data: { fullWidthMode: true },
      },
      {
        path: 'login/forgotpassword',
        component: ForgotPasswordComponent,
        data: { fullWidthMode: true },
        title: 'Forgot Password',
      },

      // Public profile access routes
      {
        path: 'e/:email',
        loadComponent: () =>
          import('./pages/public/public-profile/public-profile.component').then((m) => m.PublicProfileComponent),
        canActivate: [publicProfileGuard],
        data: { fullWidthMode: true },
      },
      {
        path: 'd/:domain',
        loadComponent: () =>
          import('./pages/public/public-profile/public-profile.component').then((m) => m.PublicProfileComponent),
        canActivate: [publicProfileGuard],
        data: { fullWidthMode: true },
      },
      {
        path: 's/:shortId',
        loadComponent: () =>
          import('./pages/public/public-profile/public-profile.component').then((m) => m.PublicProfileComponent),
        canActivate: [publicProfileGuard],
        data: { fullWidthMode: true },
      },
      // Payment Report related routes
      // DO NOT DELETE this route, it is used for the wizard to redirect to the home page
      {
        path: 'home',
        loadComponent: () =>
          import('./features/payment/public/report/page/payment-report-page.component').then(
            (m) => m.PaymentReportPageComponent,
          ),
        title: 'Payment',
        data: { fullWidthMode: true },
        canActivate: [PaymentReportGuard],
      },
      {
        path: 'payment-report/:receiverShortId',
        loadComponent: () =>
          import('./features/payment/public/report/page/payment-report-page.component').then(
            (m) => m.PaymentReportPageComponent,
          ),
        title: 'Payment',
        data: { fullWidthMode: true },
        canActivate: [PaymentReportGuard],
      },
      {
        path: 'home/:receiverShortId/:step',
        redirectTo: 'payment-report/:receiverShortId',
        title: 'Payment',
        pathMatch: 'full',
      },
      // Payment Report related routes
      // DO NOT DELETE this route, it is used for the wizard to redirect to the home page
      {
        path: 'activate-account',
        redirectTo: 'register/activate-account',
        pathMatch: 'full',
      },
      // Redirect old URL pattern to new one
      // DO NOT DELETE this route, it is used for old WHMCS integration
    ],
  },

  // Private routes (require authentication) wrapped in LayoutPrivateComponent
  {
    path: '',
    canActivate: [AuthGuard],
    canActivateChild: [AuthGuard],
    loadComponent: () =>
      import('./layouts/layout-private/layout-private.component').then((m) => m.LayoutPrivateComponent),
    children: [
      {
        path: 'contacts',
        loadComponent: () =>
          import('./pages/private/contact-card/contact-card.component').then((m) => m.ContactCardComponent),
        title: 'Contacts',
      },
      {
        path: 'overview',
        loadComponent: () =>
          import('./pages/private/dashboard-private/dashboard-private.component').then(
            (m) => m.DashboardPrivateComponent,
          ),
        title: 'Overview',
      },
      {
        path: 'transactions',
        loadComponent: () =>
          import('./pages/private/transaction-tabs/transaction-tabs.component').then((m) => m.TransactionTabsComponent),
        title: 'Transactions',
      },
      {
        path: 'billing',
        loadComponent: () =>
          import('./pages/private/billig-credit/billig-credit.component').then((m) => m.BilligCreditComponent),
        title: 'Billing',
      },
      {
        path: 'settings',
        loadComponent: () =>
          import('./pages/private/profile-settings/profile-settings.component').then((m) => m.ProfileSettingsComponent),
        children: [
          {
            path: '',
            redirectTo: 'general',
            pathMatch: 'full',
          },
          {
            path: 'general',
            loadComponent: () =>
              import('./pages/private/settings/general/general.component').then((m) => m.GeneralComponent),
            title: 'Settings - General',
          },
          {
            path: 'wallet-connections',
            loadComponent: () =>
              import('./pages/private/settings/wallet-connections/wallet-connections.component').then(
                (m) => m.WalletConnectionsComponent,
              ),
            title: 'Settings - wallets and connections',
          },
          {
            path: 'my-nicks',
            loadComponent: () =>
              import('./pages/private/settings/user-nicks/my-nicks.component').then((m) => m.UserNicksComponent),
            title: 'Settings - My Nicks',
          },
          {
            path: 'merchant-configuration',
            loadComponent: () =>
              import('./pages/private/settings/payment-conversions/payment-conversions.component').then(
                (m) => m.PaymentConversionsComponent,
              ),
            title: 'Settings - Merchant Configuration',
          },
          {
            path: 'bank-withdrawal',
            loadComponent: () =>
              import('./pages/private/settings/bank-withdrawal/bank-withdrawal.component').then(
                (m) => m.BankWithdrawalComponent,
              ),
            title: 'Settings - Bank Withdrawal',
          },
          {
            path: 'security',
            loadComponent: () =>
              import('./pages/private/settings/security/security.component').then((m) => m.SecurityComponent),
            title: 'Settings - Security',
          },
          {
            path: 'starter-kit',
            loadComponent: () =>
              import('./pages/private/settings/starter-kit/starter-kit.component').then((m) => m.StarterKitComponent),
            title: 'Settings - Starter Kit',
          },
          {
            path: 'api-key-management',
            component: ApiManagementComponent,
          },
          // Not being used now
          // {
          //   path: 'communication',
          //   loadComponent: () =>
          //     import('./pages/private/settings/communication/communication.component').then((m) => m.CommunicationComponent),
          //   title: 'Settings - Communication',
          // },
        ],
      },
      {
        path: 'payment-request',
        loadComponent: () =>
          import('./features/payment/private/request/pages/payment-request.page').then((m) => m.PaymentRequestPage),
        title: 'Payment Request',
        canActivate: [isNotAccessRestrictedBillingGuard],
        data: { fullWidthMode: true },
      },
    ],
  },

  { path: '**', redirectTo: 'error' },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      onSameUrlNavigation: 'reload',
    }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}
